import React, { useState, useMemo } from 'react';
import ToolLayout from '../components/ToolLayout';

interface GeneratorOption {
  key: string;
  name: string;
  description: string;
  icon: string;
}

const LoremIpsumGenerator: React.FC = () => {
  const [selectedGenerator, setSelectedGenerator] = useState('lorem');
  const [unitType, setUnitType] = useState<'words' | 'sentences' | 'paragraphs' | 'characters'>('paragraphs');
  const [count, setCount] = useState(3);
  const [startWithLorem, setStartWithLorem] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const generators: GeneratorOption[] = [
    {
      key: 'lorem',
      name: 'Lorem Ipsum',
      description: 'Text Lorem Ipsum cổ điển từ thế kỷ 16',
      icon: '📜'
    },
    {
      key: 'bacon',
      name: 'Bacon Ipsum',
      description: 'Lorem về thịt xông khói và các món ăn',
      icon: '🥓'
    },
    {
      key: 'cat',
      name: 'Cat Ipsum',
      description: 'Lorem về mèo và hành vi của chúng',
      icon: '🐱'
    },
    {
      key: 'hipster',
      name: 'Hipster Ipsum',
      description: 'Lorem về văn hóa hipster hiện đại',
      icon: '🎩'
    },
    {
      key: 'pirate',
      name: 'Pirate Ipsum',
      description: 'Lorem theo phong cách cướp biển',
      icon: '🏴‍☠️'
    },
    {
      key: 'vietnamese',
      name: 'Vietnamese Lorem',
      description: 'Lorem bằng tiếng Việt cho các dự án local',
      icon: '🇻🇳'
    }
  ];

  // Lorem ipsum word pools
  const wordPools = {
    lorem: [
      'lorem', 'ipsum', 'dolor', 'sit', 'amet', 'consectetur', 'adipiscing', 'elit',
      'sed', 'do', 'eiusmod', 'tempor', 'incididunt', 'ut', 'labore', 'et', 'dolore',
      'magna', 'aliqua', 'enim', 'ad', 'minim', 'veniam', 'quis', 'nostrud',
      'exercitation', 'ullamco', 'laboris', 'nisi', 'aliquip', 'ex', 'ea', 'commodo',
      'consequat', 'duis', 'aute', 'irure', 'in', 'reprehenderit', 'voluptate',
      'velit', 'esse', 'cillum', 'fugiat', 'nulla', 'pariatur', 'excepteur', 'sint',
      'occaecat', 'cupidatat', 'non', 'proident', 'sunt', 'culpa', 'qui', 'officia',
      'deserunt', 'mollit', 'anim', 'id', 'est', 'laborum', 'at', 'vero', 'eos',
      'accusamus', 'iusto', 'odio', 'dignissimos', 'ducimus', 'blanditiis',
      'praesentium', 'voluptatum', 'deleniti', 'atque', 'corrupti', 'quos', 'dolores',
      'quas', 'molestias', 'excepturi', 'occaecati', 'cupiditate', 'provident',
      'similique', 'mollitia', 'animi', 'distinctio', 'ratione', 'voluptatem',
      'sequi', 'nesciunt', 'neque', 'porro', 'quisquam', 'dolorem', 'magnam',
      'quaerat', 'voluptas', 'nemo', 'ipsam', 'aspernatur', 'aut', 'odit', 'fugit'
    ],
    bacon: [
      'bacon', 'ipsum', 'dolor', 'amet', 'hamburger', 'leberkas', 'spare', 'ribs',
      'fatback', 'brisket', 'ribeye', 'boudin', 'sirloin', 'strip', 'steak',
      'rump', 'bacon', 'meatloaf', 'tail', 'shoulder', 'short', 'loin', 'ham',
      'turkey', 'capicola', 'swine', 'kielbasa', 'andouille', 'bratwurst',
      'venison', 'salami', 'sausage', 'tenderloin', 'drumstick', 'pig', 'cow',
      'chicken', 'beef', 'pork', 'chuck', 'prosciutto', 'flank', 'jowl',
      'tri-tip', 'shank', 'bresaola', 'kevin', 'pastrami', 'meatball',
      'filet', 'mignon', 'jerky', 'pancetta', 'frankfurter', 'ball', 'tip',
      'turducken', 'corned', 'ground', 'round', 'alcatra', 'cupim', 'picanha'
    ],
    cat: [
      'meow', 'purr', 'hiss', 'scratch', 'kitty', 'whiskers', 'paws', 'tail',
      'fluffy', 'fuzzy', 'cute', 'adorable', 'playful', 'sleepy', 'hungry',
      'lazy', 'curious', 'mischievous', 'independent', 'aloof', 'affectionate',
      'climb', 'jump', 'pounce', 'stalk', 'hunt', 'chase', 'catch', 'mouse',
      'bird', 'toy', 'ball', 'string', 'catnip', 'treats', 'food', 'water',
      'litter', 'box', 'window', 'sun', 'nap', 'sleep', 'dream', 'stretch',
      'yawn', 'groom', 'lick', 'clean', 'soft', 'warm', 'cozy', 'comfortable',
      'lap', 'cuddle', 'snuggle', 'love', 'pet', 'stroke', 'rub', 'belly',
      'chin', 'ears', 'head', 'back', 'maine', 'coon', 'persian', 'siamese',
      'tabby', 'calico', 'orange', 'black', 'white', 'gray', 'striped'
    ],
    hipster: [
      'artisan', 'craft', 'organic', 'sustainable', 'ethical', 'fair', 'trade',
      'locally', 'sourced', 'small', 'batch', 'handmade', 'vintage', 'retro',
      'indie', 'alternative', 'underground', 'authentic', 'genuine', 'original',
      'unique', 'exclusive', 'limited', 'edition', 'boutique', 'curated',
      'carefully', 'selected', 'thoughtfully', 'designed', 'minimalist',
      'aesthetic', 'artisanal', 'bespoke', 'custom', 'personalized',
      'vinyl', 'records', 'turntable', 'coffee', 'espresso', 'latte', 'cappuccino',
      'pour', 'over', 'french', 'press', 'single', 'origin', 'roasted',
      'bicycle', 'fixie', 'messenger', 'bag', 'tote', 'canvas', 'leather',
      'boots', 'flannel', 'plaid', 'beanie', 'glasses', 'beard', 'mustache',
      'tattoo', 'piercing', 'ironic', 'vintage', 'thrift', 'store', 'find'
    ],
    pirate: [
      'ahoy', 'matey', 'avast', 'ye', 'landlubber', 'scurvy', 'dog', 'parrot',
      'treasure', 'chest', 'gold', 'doubloons', 'pieces', 'eight', 'ship',
      'vessel', 'crew', 'captain', 'first', 'mate', 'bosun', 'quartermaster',
      'sail', 'mast', 'rigging', 'anchor', 'cannon', 'cutlass', 'sword',
      'pistol', 'musket', 'jolly', 'roger', 'black', 'flag', 'skull', 'bones',
      'island', 'caribbean', 'sea', 'ocean', 'waves', 'storm', 'grog', 'rum',
      'ale', 'tavern', 'port', 'harbor', 'dock', 'wharf', 'plank', 'walk',
      'keelhaul', 'maroon', 'mutiny', 'rebellion', 'adventure', 'voyage',
      'expedition', 'quest', 'search', 'hunt', 'raid', 'plunder', 'loot',
      'booty', 'fortune', 'riches', 'wealthy', 'poor', 'beggar', 'thief'
    ],
    vietnamese: [
      'xin', 'chào', 'tôi', 'bạn', 'anh', 'chị', 'em', 'việt', 'nam',
      'hà', 'nội', 'hồ', 'chí', 'minh', 'thành', 'phố', 'quê', 'hương',
      'gia', 'đình', 'bố', 'mẹ', 'con', 'cái', 'nhà', 'trường', 'học',
      'sinh', 'viên', 'giáo', 'viên', 'công', 'việc', 'làm', 'thời', 'gian',
      'ngày', 'tháng', 'năm', 'tuần', 'giờ', 'phút', 'giây', 'sáng', 'chiều',
      'tối', 'đêm', 'trời', 'nắng', 'mưa', 'gió', 'lạnh', 'nóng', 'mát',
      'ấm', 'đẹp', 'xấu', 'tốt', 'xấu', 'hay', 'dở', 'lớn', 'nhỏ', 'cao',
      'thấp', 'dài', 'ngắn', 'rộng', 'hẹp', 'nhiều', 'ít', 'cũ', 'mới',
      'ăn', 'uống', 'ngủ', 'thức', 'đi', 'về', 'ra', 'vào', 'lên', 'xuống',
      'trái', 'phải', 'trước', 'sau', 'trên', 'dưới', 'trong', 'ngoài'
    ]
  };

  const generateRandomText = (generator: string, wordCount: number): string => {
    const words = wordPools[generator as keyof typeof wordPools] || wordPools.lorem;
    const result: string[] = [];

    for (let i = 0; i < wordCount; i++) {
      const randomIndex = Math.floor(Math.random() * words.length);
      result.push(words[randomIndex]);
    }

    return result.join(' ');
  };

  const generateSentence = (generator: string, wordCount: number = 8 + Math.floor(Math.random() * 10)): string => {
    let sentence = generateRandomText(generator, wordCount);
    sentence = sentence.charAt(0).toUpperCase() + sentence.slice(1) + '.';
    return sentence;
  };

  const generateParagraph = (generator: string, sentenceCount: number = 3 + Math.floor(Math.random() * 4)): string => {
    const sentences: string[] = [];
    for (let i = 0; i < sentenceCount; i++) {
      sentences.push(generateSentence(generator));
    }
    return sentences.join(' ');
  };

  const generatedText = useMemo(() => {
    let result: string[] = [];

    switch (unitType) {
      case 'words':
        const words = generateRandomText(selectedGenerator, count);
        result = [startWithLorem && selectedGenerator === 'lorem' ?
          `Lorem ipsum ${words.slice(11)}` : words];
        break;

      case 'sentences':
        for (let i = 0; i < count; i++) {
          const sentence = generateSentence(selectedGenerator);
          if (i === 0 && startWithLorem && selectedGenerator === 'lorem') {
            result.push(`Lorem ipsum dolor sit amet, ${sentence.toLowerCase()}`);
          } else {
            result.push(sentence);
          }
        }
        break;

      case 'paragraphs':
        for (let i = 0; i < count; i++) {
          let paragraph = generateParagraph(selectedGenerator);
          if (i === 0 && startWithLorem && selectedGenerator === 'lorem') {
            paragraph = `Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. ${paragraph}`;
          }
          result.push(paragraph);
        }
        break;

      case 'characters':
        let text = '';
        const prefix = startWithLorem && selectedGenerator === 'lorem'
          ? 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. '
          : '';

        if (prefix.length >= count) {
          text = prefix.slice(0, count);
        } else {
          text = prefix;

          while (text.length < count) {
            const sentence = generateSentence(selectedGenerator);
            if (text.length + sentence.length + 1 <= count) {
              text += (text.length > 0 ? ' ' : '') + sentence;
            } else {
              const remaining = count - text.length;
              text += (text.length > 0 ? ' ' : '') + sentence.slice(0, remaining);
              break;
            }
          }
        }

        result = [text];
        break;
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGenerator, unitType, count, startWithLorem]);

  const copyToClipboard = async () => {
    try {
      const text = generatedText.join('\n\n');
      await navigator.clipboard.writeText(text);
      // Could show success toast here
    } catch (err) {
      // Silent fail for clipboard API
    }
  };

  const downloadAsFile = () => {
    const text = generatedText.join('\n\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${selectedGenerator}-ipsum.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const loadPreset = (preset: { unit: 'words' | 'sentences' | 'paragraphs' | 'characters', count: number }) => {
    setUnitType(preset.unit);
    setCount(preset.count);
  };

  return (
    <ToolLayout
      title="Lorem Ipsum Generator"
      description="Tạo placeholder text cho mockups, designs và content testing với nhiều phong cách khác nhau"
    >
      <div className="lorem-generator">
        {/* Generator Selection */}
        <div className="generator-section">
          <h3>🎨 Chọn loại generator:</h3>
          <div className="generators-grid">
            {generators.map((generator) => (
              <label
                key={generator.key}
                className={`generator-card ${selectedGenerator === generator.key ? 'selected' : ''}`}
              >
                <input
                  type="radio"
                  name="generator"
                  value={generator.key}
                  checked={selectedGenerator === generator.key}
                  onChange={(e) => setSelectedGenerator(e.target.value)}
                  className="generator-radio"
                />
                <div className="generator-content">
                  <div className="generator-header">
                    <span className="generator-icon">{generator.icon}</span>
                    <span className="generator-name">{generator.name}</span>
                  </div>
                  <p className="generator-description">{generator.description}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Configuration */}
        <div className="config-section">
          <h3>⚙️ Cấu hình:</h3>
          <div className="config-grid">
            <div className="config-group">
              <label className="config-label">Loại đơn vị:</label>
              <div className="unit-buttons">
                <button
                  className={`unit-btn ${unitType === 'words' ? 'active' : ''}`}
                  onClick={() => setUnitType('words')}
                >
                  Từ
                </button>
                <button
                  className={`unit-btn ${unitType === 'sentences' ? 'active' : ''}`}
                  onClick={() => setUnitType('sentences')}
                >
                  Câu
                </button>
                <button
                  className={`unit-btn ${unitType === 'paragraphs' ? 'active' : ''}`}
                  onClick={() => setUnitType('paragraphs')}
                >
                  Đoạn
                </button>
                <button
                  className={`unit-btn ${unitType === 'characters' ? 'active' : ''}`}
                  onClick={() => setUnitType('characters')}
                >
                  Ký tự
                </button>
              </div>
            </div>

            <div className="config-group">
              <label className="config-label" htmlFor="count">
                Số lượng {unitType === 'words' ? 'từ' : unitType === 'sentences' ? 'câu' : unitType === 'paragraphs' ? 'đoạn' : 'ký tự'}:
              </label>
              <input
                id="count"
                type="number"
                min="1"
                max={unitType === 'words' ? 1000 : unitType === 'sentences' ? 50 : unitType === 'paragraphs' ? 20 : 5000}
                value={count}
                onChange={(e) => setCount(Math.max(1, parseInt(e.target.value) || 1))}
                className="count-input"
              />
            </div>

            {selectedGenerator === 'lorem' && (
              <div className="config-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={startWithLorem}
                    onChange={(e) => setStartWithLorem(e.target.checked)}
                  />
                  Bắt đầu với "Lorem ipsum..."
                </label>
              </div>
            )}
          </div>

          {/* Quick Presets */}
          <div className="presets-section">
            <label className="config-label">Preset nhanh:</label>
            <div className="presets-buttons">
              <button
                className="preset-btn"
                onClick={() => loadPreset({ unit: 'words', count: 50 })}
              >
                50 từ
              </button>
              <button
                className="preset-btn"
                onClick={() => loadPreset({ unit: 'words', count: 100 })}
              >
                100 từ
              </button>
              <button
                className="preset-btn"
                onClick={() => loadPreset({ unit: 'sentences', count: 5 })}
              >
                5 câu
              </button>
              <button
                className="preset-btn"
                onClick={() => loadPreset({ unit: 'paragraphs', count: 3 })}
              >
                3 đoạn
              </button>
              <button
                className="preset-btn"
                onClick={() => loadPreset({ unit: 'paragraphs', count: 5 })}
              >
                5 đoạn
              </button>
              <button
                className="preset-btn"
                onClick={() => loadPreset({ unit: 'characters', count: 200 })}
              >
                200 ký tự
              </button>
              <button
                className="preset-btn"
                onClick={() => loadPreset({ unit: 'characters', count: 500 })}
              >
                500 ký tự
              </button>
            </div>
          </div>
        </div>

        {/* Generated Text */}
        <div className="output-section">
          <div className="output-header">
            <h3>📝 Văn bản được tạo:</h3>
            <div className="output-actions">
              <button className="btn btn-secondary" onClick={copyToClipboard}>
                📋 Copy
              </button>
              <button className="btn btn-secondary" onClick={downloadAsFile}>
                💾 Download
              </button>
            </div>
          </div>

          <div className="output-content">
            <div className="output-stats">
              <span>
                {generatedText.join(' ').split(' ').length} từ • 
                {generatedText.join('.').split('.').length - 1} câu • 
                {generatedText.length} đoạn
              </span>
            </div>
            <div className="generated-text">
              {generatedText.map((text, index) => (
                <p key={index} className="generated-paragraph">
                  {text}
                </p>
              ))}
            </div>
          </div>
        </div>

        {/* Use Cases */}
        <div className="use-cases-section">
          <h4>💡 Cases sử dụng:</h4>
          <div className="use-cases-grid">
            <div className="use-case">
              <strong>🎨 UI/UX Design</strong>
              <p>Tạo placeholder text cho wireframes và mockups</p>
            </div>
            <div className="use-case">
              <strong>🧪 Content Testing</strong>
              <p>Test layout với độ dài nội dung khác nhau</p>
            </div>
            <div className="use-case">
              <strong>📱 App Development</strong>
              <p>Dữ liệu demo cho screens và components</p>
            </div>
            <div className="use-case">
              <strong>🌐 Web Development</strong>
              <p>Fill content cho trang web trong quá trình phát triển</p>
            </div>
            <div className="use-case">
              <strong>📊 Presentation</strong>
              <p>Nội dung placeholder cho slides và documents</p>
            </div>
            <div className="use-case">
              <strong>🎯 Marketing</strong>
              <p>Template content cho campaigns và materials</p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .lorem-generator {
          max-width: 1200px;
          margin: 0 auto;
        }

        .generator-section {
          background: var(--card-background);
          padding: 1.5rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          border: 1px solid var(--border-color);
        }

        .generator-section h3 {
          margin: 0 0 1rem 0;
          color: var(--text-primary);
        }

        .generators-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1rem;
        }

        .generator-card {
          display: block;
          padding: 1rem;
          border: 2px solid var(--border-color);
          border-radius: 8px;
          background: var(--input-background);
          cursor: pointer;
          transition: all 0.2s ease;
          position: relative;
        }

        .generator-card:hover {
          border-color: var(--primary-color);
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        }

        .generator-card.selected {
          border-color: var(--primary-color);
          background: rgba(66, 153, 225, 0.05);
        }

        .generator-radio {
          position: absolute;
          top: 0.75rem;
          right: 0.75rem;
          margin: 0;
        }

        .generator-content {
          padding-right: 2rem;
        }

        .generator-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
        }

        .generator-icon {
          font-size: 1.5rem;
        }

        .generator-name {
          font-weight: 600;
          color: var(--text-primary);
        }

        .generator-description {
          margin: 0;
          color: var(--text-secondary);
          font-size: 0.9rem;
          line-height: 1.4;
        }

        .config-section {
          background: var(--card-background);
          padding: 1.5rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          border: 1px solid var(--border-color);
        }

        .config-section h3 {
          margin: 0 0 1rem 0;
          color: var(--text-primary);
        }

        .config-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .config-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .config-label {
          font-weight: 600;
          color: var(--text-primary);
        }

        .unit-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .unit-btn {
          padding: 0.5rem 1rem;
          border: 1px solid var(--border-color);
          border-radius: 6px;
          background: var(--input-background);
          color: var(--text-primary);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .unit-btn.active {
          background: var(--primary-color);
          color: white;
          border-color: var(--primary-color);
        }

        .unit-btn:hover:not(.active) {
          border-color: var(--primary-color);
        }

        .count-input {
          padding: 0.5rem;
          border: 1px solid var(--border-color);
          border-radius: 6px;
          background: var(--input-background);
          color: var(--text-primary);
          max-width: 100px;
        }

        .count-input:focus {
          outline: none;
          border-color: var(--primary-color);
          box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: var(--text-primary);
        }

        .checkbox-label input[type="checkbox"] {
          margin: 0;
        }

        .presets-section {
          border-top: 1px solid var(--border-color);
          padding-top: 1rem;
        }

        .presets-buttons {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .preset-btn {
          padding: 0.5rem 1rem;
          border: 1px solid var(--border-color);
          border-radius: 6px;
          background: var(--input-background);
          color: var(--text-primary);
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 0.9rem;
        }

        .preset-btn:hover {
          background: var(--primary-color);
          color: white;
          border-color: var(--primary-color);
        }

        .output-section {
          background: var(--card-background);
          padding: 1.5rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          border: 1px solid var(--border-color);
        }

        .output-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .output-header h3 {
          margin: 0;
          color: var(--text-primary);
        }

        .output-actions {
          display: flex;
          gap: 0.5rem;
        }

        .output-content {
          border: 1px solid var(--border-color);
          border-radius: 6px;
          overflow: hidden;
        }

        .output-stats {
          padding: 0.75rem;
          background: var(--input-background);
          border-bottom: 1px solid var(--border-color);
          color: var(--text-secondary);
          font-size: 0.9rem;
        }

        .generated-text {
          padding: 1rem;
          background: var(--input-background);
          max-height: 400px;
          overflow-y: auto;
          line-height: 1.7;
          color: var(--text-primary);
        }

        .generated-paragraph {
          margin: 0 0 1rem 0;
          text-align: justify;
        }

        .generated-paragraph:last-child {
          margin-bottom: 0;
        }

        .use-cases-section {
          background: var(--card-background);
          padding: 1.5rem;
          border-radius: 8px;
          border: 1px solid var(--border-color);
        }

        .use-cases-section h4 {
          margin: 0 0 1rem 0;
          color: var(--text-primary);
        }

        .use-cases-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1rem;
        }

        .use-case {
          padding: 1rem;
          background: var(--input-background);
          border-radius: 6px;
          border: 1px solid var(--border-color);
        }

        .use-case strong {
          color: var(--text-primary);
          display: block;
          margin-bottom: 0.5rem;
        }

        .use-case p {
          margin: 0;
          color: var(--text-secondary);
          font-size: 0.9rem;
          line-height: 1.4;
        }

        .btn {
          padding: 0.5rem 1rem;
          border: 1px solid var(--border-color);
          border-radius: 6px;
          background: var(--input-background);
          color: var(--text-primary);
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 0.9rem;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .btn-secondary {
          background: var(--input-background);
          color: var(--text-primary);
        }

        .btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        @media (max-width: 768px) {
          .generators-grid {
            grid-template-columns: 1fr;
          }

          .config-grid {
            grid-template-columns: 1fr;
          }

          .unit-buttons, .presets-buttons {
            flex-wrap: wrap;
          }

          .use-cases-grid {
            grid-template-columns: 1fr;
          }

          .output-header {
            flex-direction: column;
            align-items: stretch;
          }

          .output-actions {
            justify-content: center;
          }
        }
      `}</style>
    </ToolLayout>
  );
};

export default LoremIpsumGenerator;


